import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config/env';
import { renderCategoryIcon } from '../utils/categoryIcons';
import { Edit2, ToggleLeft, ToggleRight, Trash2, X, Plus, BarChart, TrendingUp, TrendingDown, Briefcase, School, Building, Building2, Landmark, Target, Lightbulb, Search, ClipboardList, CheckCircle, XCircle, AlertTriangle, MessageSquare, Users, User, DollarSign, GraduationCap, Globe, Rocket, Star, Heart, Mail, Smartphone, Laptop, ShoppingCart, Gift, Trophy, Bell, Lock, Settings } from 'lucide-react';
import './SurveyTypeManagement.css';

interface SurveyType {
  id: string;
  name: string;
  description: string;
  icon?: string;
  color: string;
  orderIndex: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const SurveyTypeManagement: React.FC = () => {
  const { user, hasPermission, centerInfo, selectedCenterId } = useAuth();
  const [surveyTypes, setSurveyTypes] = useState<SurveyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<SurveyType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#667eea',
    orderIndex: 0,
    isActive: true
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSurveyTypes();
  }, [selectedCenterId]); // selectedCenterId가 변경될 때마다 재조회

  const loadSurveyTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const centerUserId = selectedCenterId || user?.centerId || centerInfo?.centerId;
      const url = centerUserId 
        ? `${getBackendUrl()}/api/survey-categories?centerUserId=${centerUserId}`
        : `${getBackendUrl()}/api/survey-categories`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSurveyTypes(data || []);
    } catch (error) {
      console.error('Failed to load survey types:', error);
      setError('설문 유형을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      icon: '',
      color: '#667eea',
      orderIndex: 0,
      isActive: true
    });
    setShowModal(true);
  };

  const handleEdit = (surveyType: SurveyType) => {
    setEditingType(surveyType);
    setFormData({
      name: surveyType.name,
      description: surveyType.description || '',
      icon: surveyType.icon || '',
      color: surveyType.color,
      orderIndex: surveyType.orderIndex,
      isActive: surveyType.isActive
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const url = editingType 
        ? `${getBackendUrl()}/api/survey-categories/${editingType.id}`
        : `${getBackendUrl()}/api/survey-categories`;
      
      const bodyData = {
        ...formData,
        createdBy: String(user?.userid || user?.email || 'external-user'),
        centerUserId: selectedCenterId || user?.centerId || centerInfo?.centerId || null
      };
      
      const response = await fetch(url, {
        method: editingType ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (editingType) {
        const updatedTypes = surveyTypes.map(type => 
          type.id === editingType.id ? data : type
        );
        setSurveyTypes(updatedTypes);
      } else {
        setSurveyTypes([...surveyTypes, data]);
      }
      
      setShowModal(false);
      setFormData({ name: '', description: '', icon: '', color: '#667eea', orderIndex: 0, isActive: true });
    } catch (error) {
      console.error('Failed to save survey type:', error);
      setError('설문 유형 저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('이 설문 유형을 삭제하시겠습니까?')) {
      setError(null);
      try {
        const response = await fetch(`${getBackendUrl()}/api/survey-categories/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setSurveyTypes(surveyTypes.filter(type => type.id !== id));
      } catch (error) {
        console.error('Failed to delete survey type:', error);
        setError('설문 유형 삭제에 실패했습니다.');
      }
    }
  };

  const toggleActive = async (id: string) => {
    setError(null);
    try {
      const surveyType = surveyTypes.find(type => type.id === id);
      if (!surveyType) return;

      const response = await fetch(`${getBackendUrl()}/api/survey-categories/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !surveyType.isActive }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const updatedTypes = surveyTypes.map(type => 
        type.id === id ? data : type
      );
      setSurveyTypes(updatedTypes);
    } catch (error) {
      console.error('Failed to toggle survey type status:', error);
      setError('설문 유형 상태 변경에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="survey-type-management">
        <div className="loading">설문 유형을 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="survey-type-management">
      <div className="survey-list-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Settings size={32} />
            설문 유형 관리
          </h1>
          <p className="page-description">설문조사 카테고리를 추가하고 관리하세요</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={handleCreateNew}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Plus size={20} />
          새 유형 추가
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ 
          backgroundColor: '#fee', 
          color: '#c00', 
          padding: '10px', 
          marginBottom: '20px', 
          borderRadius: '4px' 
        }}>
          {error}
        </div>
      )}

      <div className="survey-types-grid">
        {surveyTypes.map((type) => (
          <div key={type.id} className={`survey-type-card ${!type.isActive ? 'inactive' : ''}`}>
            <div className="card-header">
              <div 
                className="type-color-indicator" 
                style={{ backgroundColor: type.color }}
              ></div>
              <div className="card-actions">
                <button 
                  className="btn-icon"
                  onClick={() => handleEdit(type)}
                  title="편집"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="btn-icon"
                  onClick={() => toggleActive(type.id)}
                  title={type.isActive ? '비활성화' : '활성화'}
                >
                  {type.isActive ? <ToggleRight size={16} color="#22c55e" /> : <ToggleLeft size={16} color="#ef4444" />}
                </button>
                <button 
                  className="btn-icon delete"
                  onClick={() => handleDelete(type.id)}
                  title="삭제"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <div className="card-content">
              <h3>{type.name}</h3>
              <p>{type.description || '설명이 없습니다.'}</p>
              <div className="card-meta">
                <span className={`status ${type.isActive ? 'active' : 'inactive'}`}>
                  {type.isActive ? '활성' : '비활성'}
                </span>
                <span className="date">
                  생성: {new Date(type.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingType ? '설문 유형 편집' : '새 설문 유형 추가'}</h2>
              <button 
                className="btn-close"
                onClick={() => setShowModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">유형 이름</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="설문 유형 이름을 입력하세요"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">설명</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  placeholder="설문 유형에 대한 설명을 입력하세요"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="color">색상</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    id="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                  <span className="color-preview" style={{ backgroundColor: formData.color }}></span>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="icon">아이콘</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    id="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="이모지를 선택하거나 입력하세요"
                    style={{ flex: 1 }}
                  />
                  {formData.icon && (
                    <div className="icon-preview">
                      {renderCategoryIcon(formData.icon, 24)}
                    </div>
                  )}
                </div>
                <div className="icon-grid">
                  {[
                    { id: 'chart', icon: BarChart, label: '차트' },
                    { id: 'trending-up', icon: TrendingUp, label: '상승' },
                    { id: 'trending-down', icon: TrendingDown, label: '하락' },
                    { id: 'briefcase', icon: Briefcase, label: '비즈니스' },
                    { id: 'school', icon: School, label: '학교' },
                    { id: 'health', icon: Building, label: '병원' },
                    { id: 'landmark', icon: Landmark, label: '공공기관' },
                    { id: 'building', icon: Building2, label: '건물' },
                    { id: 'target', icon: Target, label: '목표' },
                    { id: 'lightbulb', icon: Lightbulb, label: '아이디어' },
                    { id: 'search', icon: Search, label: '검색' },
                    { id: 'clipboard', icon: ClipboardList, label: '목록' },
                    { id: 'check', icon: CheckCircle, label: '완료' },
                    { id: 'x', icon: XCircle, label: '취소' },
                    { id: 'alert', icon: AlertTriangle, label: '경고' },
                    { id: 'message', icon: MessageSquare, label: '메시지' },
                    { id: 'users', icon: Users, label: '사용자들' },
                    { id: 'user', icon: User, label: '사용자' },
                    { id: 'dollar', icon: DollarSign, label: '금액' },
                    { id: 'graduation', icon: GraduationCap, label: '교육' },
                    { id: 'globe', icon: Globe, label: '글로벌' },
                    { id: 'rocket', icon: Rocket, label: '시작' },
                    { id: 'star', icon: Star, label: '즐겨찾기' },
                    { id: 'heart', icon: Heart, label: '좋아요' },
                    { id: 'mail', icon: Mail, label: '이메일' },
                    { id: 'phone', icon: Smartphone, label: '모바일' },
                    { id: 'laptop', icon: Laptop, label: '컴퓨터' },
                    { id: 'cart', icon: ShoppingCart, label: '쇼핑' },
                    { id: 'gift', icon: Gift, label: '선물' },
                    { id: 'trophy', icon: Trophy, label: '상' },
                    { id: 'bell', icon: Bell, label: '알림' },
                    { id: 'lock', icon: Lock, label: '보안' }
                  ].map(({ id, icon: Icon, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: id })}
                      title={label}
                      className={`icon-button ${formData.icon === id ? 'selected' : ''}`}
                    >
                      <Icon size={20} />
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="orderIndex">순서</label>
                <input
                  type="number"
                  id="orderIndex"
                  value={formData.orderIndex}
                  onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="표시 순서를 입력하세요"
                />
              </div>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span className="checkbox-text">활성 상태</span>
                </label>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingType ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyTypeManagement;